import { Box, FormControl, InputAdornment, TextField, TextFieldProps } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { ControllerRenderProps, FieldError, FieldValues, Path } from 'react-hook-form';

import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { useCustomFieldStyles } from '@/components/shared/components/NewContextForms/hooks/useCustomFieldStyles';
import {
  CustomTextFieldLabel,
  CustomTextFieldLabelProps,
} from '@/components/shared/components/NewContextForms/shared/CustomTextFieldLabel';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

export interface CustomTextFieldExtraProps<TFieldValues> extends CustomTextFieldLabelProps {
  valueStartIcon?: JSX.Element | string;
  valueUnit?: React.ReactNode | string;
  fieldVariant?: FieldVariant;
  wrap?: boolean;
  labelSuffix?: string | number | JSX.Element;
  copyToClipboard?: boolean;
  underlined?: boolean;
  hint?: string;
  field?: ControllerRenderProps<FieldValues, Path<TFieldValues>>;
  error?: FieldError | undefined;
  onChangeCallback?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  className?: string;
  disableTranslation?: boolean;
  boxProps?: React.ComponentProps<typeof Box>;
}

export interface CustomTextFieldProps<TFieldValues extends FieldValues>
  extends Omit<TextFieldProps, 'name' | 'error' | 'label'>,
    CustomTextFieldExtraProps<TFieldValues> {}

export function CustomTextField<TFieldValues extends FieldValues>({
  field,
  fieldVariant = FieldVariant.dataRow,
  error,
  size = 'small',
  fullWidth = true,
  underlined,
  multiline,

  // label props
  label,
  labelDynamicDataTexts,
  strongTitle,
  labelIcon,
  labelIconPosition,
  forceSingleLine,
  tooltipTitle,
  markAsOptional,
  disableTranslation,
  labelSuffix,
  highlightLabel,

  // other
  onChangeCallback,
  onBlur,
  hint,
  className,
  ...props
}: CustomTextFieldProps<TFieldValues>) {
  const { customBorderRadius } = useCustomTheme();

  const { getMUITextFieldClasses } = useCustomFieldStyles({
    fieldVariant,
    field: !!field,
    disabled: props.disabled,
    underlined,
    highlightLabel,
  });

  const fieldVariantIsForm = fieldVariant === FieldVariant.form;

  const labelProps = {
    label,
    labelDynamicDataTexts,
    labelIcon,
    labelIconPosition,
    tooltipTitle,
    strongTitle,
    forceSingleLine,
    markAsOptional,
    disableTranslation,
    labelSuffix,
    highlightLabel,
  };

  const getStartAdornment = () => {
    if (!fieldVariantIsForm) {
      return (
        <InputAdornment position="start">
          <CustomTextFieldLabel {...labelProps}>{props.InputProps?.startAdornment}</CustomTextFieldLabel>
        </InputAdornment>
      );
    }
    if (props.InputProps?.startAdornment) {
      return <InputAdornment position="start">{props.InputProps?.startAdornment}</InputAdornment>;
    }
    return null;
  };

  const textFieldClasses = getMUITextFieldClasses();
  const startAdornment = getStartAdornment();

  return (
    <FormControl fullWidth classes={textFieldClasses} className={className}>
      <TextField
        maxRows={4}
        {...props}
        {...(!field && !!props.defaultValue && { defaultValue: props.defaultValue })}
        label={
          fieldVariantIsForm && (label || labelIcon || tooltipTitle) ? <CustomTextFieldLabel {...labelProps} /> : null
        }
        variant={'standard'}
        fullWidth={fullWidth}
        size={size}
        multiline={multiline || (!!label && !fieldVariantIsForm)}
        InputLabelProps={{
          shrink: fieldVariantIsForm,
          style: { display: 'block', width: '100%', maxWidth: '100%' },
          ...props?.InputLabelProps,
        }}
        InputProps={{
          ...props?.InputProps,
          classes: textFieldClasses,
          startAdornment,
          endAdornment: props.InputProps?.endAdornment && (
            <InputAdornment position="end">{props.InputProps?.endAdornment}</InputAdornment>
          ),
          inputProps: {
            ...props?.InputProps?.inputProps,
            ...props?.inputProps,
            style: {
              ...props?.InputProps?.inputProps?.style,
              ...props?.inputProps?.style,
              ...props?.style,
            },
          },
        }}
        {...field}
        {...(error && {
          errors: !!error,
          helperText: error?.message,
        })}
        {...(onChangeCallback && {
          onChange: (e) => {
            field?.onChange(e);
            onChangeCallback(e);
          },
        })}
        {...(onBlur && {
          onBlur: (e) => {
            field?.onBlur();
            onBlur(e);
          },
        })}
      />
      {hint && (
        <Alert
          severity={'info'}
          icon={<Icon size={22}>{'info'}</Icon>}
          style={{ borderRadius: customBorderRadius.paper, marginTop: '12px' }}
        >
          <Font variant={'caption'} color={'title'}>
            <Text tId={hint} />
          </Font>
        </Alert>
      )}
    </FormControl>
  );
}
